// src/components/ResourceNodeDetailModal.tsx
import React, {useMemo, useState, useEffect, useCallback} from 'react';
import {Fuel, Pickaxe, Trees, Droplet, Flame, TrendingUp, Spline} from 'lucide-react';
import {useSimulationStore, ResourceInvestment} from '../../store/useSimulationStore';
import DetailModal, {KpiItem, ModalSection} from '../common/DetailModal';
import {
   ChartModeToggle,
   NormalizedInfo,
   useChartNormalization,
   ChartMode,
   useChartRange,
   ChartRangeSlider
} from '../common/ChartModeToggle';
import {authHeaders} from '../../store/useAuthStore';
import {apiUrl} from '../../utils/api';
import {
   ComposedChart, LineChart, Line, XAxis, YAxis, CartesianGrid,
   Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart,
} from 'recharts';
import StatusBadge, {BadgePreset} from "../common/StatusBadge";
import ItemList, {ItemRowProps} from '../common/ItemList';
import ProgressBar from '../common/ProgressBar';
import {cardSubtle} from '../../utils/styles';

const TYPE_META: Record<string, {
   icon: React.ReactNode;
   color: string;
   bg: string;
   border: string;
   label: string,
   hex: string
}> = {
   oil: {
      icon: <Fuel className="w-7 h-7 md:w-9 md:h-9" />,
      color: 'text-amber-400',
      bg: 'bg-amber-900/20',
      border: 'border-amber-800/50',
      label: 'Oil Deposit',
      hex: '#f59e0b'
   },
   minerals: {
      icon: <Pickaxe className="w-7 h-7 md:w-9 md:h-9" />,
      color: 'text-violet-400',
      bg: 'bg-violet-900/20',
      border: 'border-violet-800/50',
      label: 'Mineral Deposit',
      hex: '#a78bfa'
   },
   forest: {
      icon: <Trees className="w-7 h-7 md:w-9 md:h-9" />,
      color: 'text-emerald-400',
      bg: 'bg-emerald-900/20',
      border: 'border-emerald-800/50',
      label: 'Timberland',
      hex: '#34d399'
   },
   water: {
      icon: <Droplet className="w-7 h-7 md:w-9 md:h-9" />,
      color: 'text-sky-400',
      bg: 'bg-sky-900/20',
      border: 'border-sky-800/50',
      label: 'Water Source',
      hex: '#38bdf8'
   },
   gas: {
      icon: <Flame className="w-7 h-7 md:w-9 md:h-9" />,
      color: 'text-orange-400',
      bg: 'bg-orange-900/20',
      border: 'border-orange-800/50',
      label: 'Gas Field',
      hex: '#fb923c'
   },
   generic: {
      icon: <span>◆</span>,
      color: 'text-gray-400',
      bg: 'bg-gray-800',
      border: 'border-gray-700',
      label: 'Resource Deposit',
      hex: '#94a3b8'
   },
};

// ─── Metric config ────────────────────────────────────────────────────────────
const METRICS = {
   current_amount: {
      label: 'Reserves',
      color: '#10b981',
      format: (v: number) => v.toLocaleString(undefined, {maximumFractionDigits: 0})
   },
   yield_per_tick: {label: 'Yield / tick', color: '#f59e0b', format: (v: number) => `+${v.toLocaleString()}`},
   regen_rate: {label: 'Regen / tick', color: '#38bdf8', format: (v: number) => `+${v.toLocaleString()}`},
   capacity: {
      label: 'Capacity',
      color: '#8b5cf6',
      format: (v: number) => v.toLocaleString(undefined, {maximumFractionDigits: 0})
   },
} as const;
type MetricKey = keyof typeof METRICS;

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({active, payload, label, chartData, visibleMetrics, normalized}: any) => {
   if (!active || !payload?.length) return null;
   const raw = chartData.find((d: any) => d.tick === label) ?? {};
   return (
      <div className={`${cardSubtle} p-3 shadow-2xl text-xs min-w-[170px]`}>
         <div className="text-gray-400 mb-2 font-medium flex justify-between">
            <span>Tick {label}</span>
            {normalized && <span className="text-gray-600 italic">normalized view</span>}
         </div>
         {(Object.keys(METRICS) as MetricKey[]).map(key => {
            if (!visibleMetrics[key]) return null;
            const cfg = METRICS[key];
            const rawValue = raw[key];
            if (rawValue === undefined) return null;
            return (
               <div key={key} className="flex justify-between gap-3 mb-1">
                  <span style={{color: cfg.color}} className="font-medium">{cfg.label}</span>
                  <span className="text-white font-mono">{cfg.format(rawValue)}</span>
               </div>
            );
         })}
      </div>
   );
};

// ─── Main component ───────────────────────────────────────────────────────────
const BONUS_POOL = 1000;
const MAX_SHARES = 100;

function investorBonusYield(shares: number, maxShares: number = MAX_SHARES): number {
   return BONUS_POOL * Math.sqrt(shares / Math.max(maxShares, 1));
}

const ResourceNodeDetailModal: React.FC = () => {
   const resourceNodes = useSimulationStore(s => s.resourceNodes);
   const nodes = useSimulationStore(s => s.nodes);
   const investments = useSimulationStore(s => s.investments);
   const selectedResourceNodeId = useSimulationStore(s => s.selectedResourceNodeId);
   const setSelectedResourceNodeId = useSimulationStore(s => s.setSelectedResourceNodeId);
   const setSelectedNodeId = useSimulationStore(s => s.setSelectedNodeId);
   
   const [history, setHistory] = useState<any[]>([]);
   const [isLoadingHistory, setIsLoadingHistory] = useState(false);
   const [visibleMetrics, setVisibleMetrics] = useState<Record<MetricKey, boolean>>({
      current_amount: true, yield_per_tick: true, regen_rate: false, capacity: false,
   });
   const [chartMode, setChartMode] = useState<ChartMode>('absolute');
   
   const chartData = useMemo(() => history.map((h: any) => ({
      tick: h.tick,
      current_amount: Math.round(h.current_amount ?? 0),
      yield_per_tick: h.yield_per_tick ?? 0,
      regen_rate: h.regen_rate ?? 0,
      capacity: Math.round(h.capacity ?? 0),
   })), [history]);
   
   const {range, setRange, sliced: rangedData} = useChartRange(chartData);
   const metricKeys = useMemo(() => Object.keys(METRICS), []);
   const {normalizedData} = useChartNormalization(rangedData, metricKeys);
   const displayData = chartMode === 'normalized' ? normalizedData : rangedData;
   
   const toggleMetric = (key: MetricKey) =>
      setVisibleMetrics(prev => ({...prev, [key]: !prev[key]}));
   
   const fetchHistory = useCallback(() => {
      if (!selectedResourceNodeId) return;
      setIsLoadingHistory(true);
      fetch(apiUrl(`/api/resources/${selectedResourceNodeId}/history`), {headers: authHeaders()})
         .then(res => res.json())
         .then(data => {
            setHistory(data);
            setIsLoadingHistory(false);
         })
         .catch(() => setIsLoadingHistory(false));
   }, [selectedResourceNodeId]);
   
   useEffect(() => {
      fetchHistory();
   }, [fetchHistory]);
   
   useEffect(() => {
      if (!selectedResourceNodeId) return;
      const interval = setInterval(fetchHistory, 25000);
      return () => clearInterval(interval);
   }, [fetchHistory, selectedResourceNodeId]);
   
   const rn = resourceNodes.find(r => r.id === selectedResourceNodeId);

   const nodesInRange = useMemo(() => {
      if (!rn) return [];
      return nodes
         .map(n => ({
            node: n,
            dist: Math.sqrt((n.x - rn.x) ** 2 + (n.y - rn.y) ** 2),
         }))
         .filter(({dist}) => dist <= rn.radius)
         .sort((a, b) => a.dist - b.dist);
   }, [nodes, rn]);

   const rnInvestments = useMemo(() =>
      investments.filter(inv => inv.resource_id === selectedResourceNodeId),
      [investments, selectedResourceNodeId]
   );

   const totalSharesSold = useMemo(() =>
      rnInvestments.reduce((s, inv) => s + inv.shares, 0),
      [rnInvestments]
   );

   // Yield curve data for mini chart: 0..maxShares
   const yieldCurveData = useMemo(() => {
      const maxSh = rn?.max_total_shares ?? MAX_SHARES;
      const points = [];
      for (let s = 0; s <= maxSh; s += Math.max(1, Math.floor(maxSh / 20))) {
         points.push({shares: s, bonus: Math.round(investorBonusYield(s, maxSh))});
      }
      if (points[points.length - 1]?.shares !== maxSh)
         points.push({shares: maxSh, bonus: Math.round(investorBonusYield(maxSh, maxSh))});
      return points;
   }, [rn?.max_total_shares]);

   if (!rn) return null;

   const maxShares = rn.max_total_shares ?? MAX_SHARES;

   const rangeItems: ItemRowProps[] = nodesInRange.map(({node, dist}) => {
      const inv = rnInvestments.find(i => i.node_id === node.id);
      const nodeShares = inv?.shares ?? 0;
      const baseYield = 100;
      const bonusYield = nodeShares > 0 ? Math.round(investorBonusYield(nodeShares, maxShares)) : 0;
      const totalYield = baseYield + bonusYield;
      return {
         id: node.id,
         label: node.name,
         badge: node.income_status,
         sub: nodeShares > 0
            ? `${nodeShares} shares (${((nodeShares / maxShares) * 100).toFixed(0)}%)  ·  ${dist.toFixed(0)} units`
            : `no shares  ·  ${dist.toFixed(0)} units`,
         value: `+${totalYield} /tick`,
         valueColor: nodeShares > 0 ? 'text-emerald-400' : 'text-gray-500',
         accentColor: nodeShares > 0 ? TYPE_META[rn.type].hex : '#374151',
         variant: 'card' as const,
         onClick: () => {
            setSelectedNodeId(node.id);
            setSelectedResourceNodeId(null);
         },
      };
   });
   
   const meta = TYPE_META[rn.type] ?? TYPE_META.generic;
   const baseTotal = nodesInRange.length * 100;
   const bonusTotal = rnInvestments.reduce((sum, inv) => sum + investorBonusYield(inv.shares, maxShares), 0);
   const totalYieldPerTick = Math.round(baseTotal + bonusTotal);
   const pct = rn.capacity > 0 ? (rn.current_amount / rn.capacity) * 100 : 100;
   const saturationPct = maxShares > 0 ? (totalSharesSold / maxShares) * 100 : 0;
   
   const kpis: KpiItem[] = [
      {
         label: 'Yield / tick',
         value: `+${rn.yield_per_tick}`,
         color: 'text-emerald-400',
         active: visibleMetrics.yield_per_tick,
         onClick: () => toggleMetric('yield_per_tick')
      },
      {
         label: 'Reserves',
         value: `${pct.toFixed(0)}%`,
         color: pct > 50 ? 'text-emerald-400' : pct > 20 ? 'text-yellow-400' : 'text-red-400',
         active: visibleMetrics.current_amount,
         onClick: () => toggleMetric('current_amount')
      },
      {
         label: 'Regen / tick',
         value: `+${rn.regen_rate}`,
         color: 'text-sky-400',
         active: visibleMetrics.regen_rate,
         onClick: () => toggleMetric('regen_rate')
      },
      {
         label: 'Capacity',
         value: rn.capacity.toLocaleString(),
         color: 'text-violet-400',
         active: visibleMetrics.capacity,
         onClick: () => toggleMetric('capacity')
      },
   ];
   
   return (
      <DetailModal
         onClose={() => setSelectedResourceNodeId(null)}
         maxWidth="max-w-3xl"
         header={{
            icon: meta.icon,
            typeLabel: meta.label,
            title: rn.name,
            subtitle: (
               <div className="text-xs font-mono text-gray-400 mt-0.5">
                  x: {rn.x} · y: {rn.y} · z: {rn.z}
               </div>
            ),
            bgClass: meta.bg,
            borderClass: meta.border,
         }}
         kpis={kpis}
         kpiHint=""
      >
         {/* ── History chart ── */}
         <ModalSection
            title="History"
            icon={<TrendingUp className="w-4 h-4" />}
            titleExtra={
               <div className="flex items-center gap-3">
                  {isLoadingHistory && (
                     <span className="text-xs text-gray-500 animate-pulse">refreshing…</span>
                  )}
                  <span className="text-xs text-gray-500">
                     {chartData.length} snapshots
                     <ChartRangeSlider range={range} onChange={setRange} totalPoints={chartData.length} />
                  </span>
                  <ChartModeToggle mode={chartMode} onModeChange={setChartMode} />
               </div>
            }
         >
            <NormalizedInfo show={chartMode === 'normalized'} />
            {chartData.length === 0 ? (
               <div className="h-48 flex items-center justify-center text-gray-600 text-sm">
                  {isLoadingHistory ? 'Loading history…' : 'No snapshot data yet. Snapshots are saved every 5 ticks.'}
               </div>
            ) : (
               <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={displayData} margin={{
                     top: 8,
                     right: chartMode === 'absolute' ? 40 : 16,
                     bottom: 0,
                     left: 8
                  }}>
                     <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                     <XAxis
                        dataKey="tick"
                        tick={{fill: '#6b7280', fontSize: 11}}
                        axisLine={false}
                        tickLine={false}
                        label={{
                           value: 'tick',
                           position: 'insideBottomRight',
                           offset: -4,
                           fill: '#4b5563',
                           fontSize: 10
                        }}
                     />
                     {chartMode === 'absolute' ? (
                        <YAxis
                           yAxisId="left"
                           tick={{fill: '#6b7280', fontSize: 10}}
                           axisLine={false}
                           tickLine={false}
                           width={50}
                           tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                        />
                     ) : (
                        <YAxis
                           yAxisId="norm"
                           domain={[0, 100]}
                           tick={{fill: '#6b7280', fontSize: 10}}
                           axisLine={false}
                           tickLine={false}
                           tickFormatter={v => `${v}%`}
                           width={36}
                        />
                     )}
                     {chartMode === 'normalized' && [25, 50, 75].map(y => (
                        <ReferenceLine key={y} yAxisId="norm" y={y} stroke="#374151" strokeDasharray="3 3" strokeOpacity={0.5} />
                     ))}
                     <Tooltip content={
                        <CustomTooltip chartData={chartData} visibleMetrics={visibleMetrics} normalized={chartMode === 'normalized'} />} />
                     {(Object.keys(METRICS) as MetricKey[]).map(key => (
                        visibleMetrics[key] ? (
                           <Line
                              key={key}
                              yAxisId={chartMode === 'absolute' ? 'left' : 'norm'}
                              type="monotone"
                              dataKey={key}
                              stroke={METRICS[key].color}
                              strokeWidth={2}
                              dot={false}
                              name={METRICS[key].label}
                              isAnimationActive={false}
                           />
                        ) : null
                     ))}
                  </ComposedChart>
               </ResponsiveContainer>
            )}
         </ModalSection>
         
         {/* ── Depletion bar ── */}
         <div className={`${cardSubtle} px-4 py-3`}>
            <div className="flex justify-between text-sm mb-2">
               <span className="text-gray-400">Resource Reserves</span>
               <span className={`font-mono font-bold ${pct > 50 ? 'text-emerald-400' : pct > 20 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {Math.round(rn.current_amount).toLocaleString()} / {Math.round(rn.capacity).toLocaleString()}
               </span>
            </div>
            <ProgressBar
               value={Math.max(1, pct)}
               color={pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-yellow-500' : 'bg-red-500'}
               height="h-3"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1 font-mono">
               <span>Regen: +{rn.regen_rate}/tick</span>
               <span>Extraction: -{totalYieldPerTick.toLocaleString()}/tick</span>
            </div>
         </div>
         
         {/* ── Total output ── */}
         <div className={`${cardSubtle} px-4 py-3 flex items-center justify-between`}>
            <span className="text-sm text-gray-400">Total output this tick</span>
            <span className="text-lg font-bold font-mono text-emerald-400">
               +{totalYieldPerTick.toLocaleString()} resources
            </span>
         </div>
         
         {/* ── Investment saturation ── */}
         <div className={`${cardSubtle} px-4 py-3`}>
            <div className="flex justify-between text-sm mb-2">
               <span className="text-gray-400">Share Saturation</span>
               <span className="font-mono font-bold text-violet-400">
                  {totalSharesSold} / {maxShares} shares sold
               </span>
            </div>
            <ProgressBar
               value={Math.max(0, saturationPct)}
               color="bg-violet-500"
               height="h-2"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1 font-mono">
               <span>{rnInvestments.length} investor{rnInvestments.length !== 1 ? 's' : ''}</span>
               <span>{(maxShares - totalSharesSold)} shares available</span>
            </div>
         </div>

         {/* ── Yield curve chart ── */}
         <ModalSection title="Investor Yield Curve" icon={<Spline className="w-4 h-4" />}>
            <div className="text-xs text-gray-500 mb-2">
               Bonus yield per node based on shares owned (diminishing returns)
            </div>
            <ResponsiveContainer width="100%" height={140}>
               <AreaChart data={yieldCurveData} margin={{top: 4, right: 16, bottom: 0, left: 8}}>
                  <defs>
                     <linearGradient id="bonusGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                     </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                  <XAxis dataKey="shares" tick={{fill: '#6b7280', fontSize: 10}} axisLine={false} tickLine={false}
                     label={{value: 'shares', position: 'insideBottomRight', offset: -4, fill: '#4b5563', fontSize: 10}} />
                  <YAxis tick={{fill: '#6b7280', fontSize: 10}} axisLine={false} tickLine={false} width={40}
                     tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)} />
                  {rnInvestments.map(inv => (
                     <ReferenceLine key={inv.node_id} x={inv.shares} stroke={meta.hex}
                        strokeDasharray="3 3" strokeOpacity={0.7}
                        label={{value: nodes.find(n => n.id === inv.node_id)?.name ?? `#${inv.node_id}`,
                           position: 'top', fill: meta.hex, fontSize: 9}} />
                  ))}
                  <Tooltip
                     contentStyle={{background: '#111827', border: '1px solid #374151', fontSize: 11}}
                     formatter={(v: any) => [`+${v} bonus`, 'Yield']}
                     labelFormatter={(l: any) => `${l} shares`}
                  />
                  <Area type="monotone" dataKey="bonus" stroke="#8b5cf6" strokeWidth={2}
                     fill="url(#bonusGrad)" dot={false} isAnimationActive={false} />
               </AreaChart>
            </ResponsiveContainer>
         </ModalSection>

         {/* ── Nodes in range ── */}
         <ModalSection title={`Nodes receiving yield (${nodesInRange.length})`}>
            {nodesInRange.length === 0 ? (
               <div className="text-center py-8 text-gray-600 text-sm">
                  No nodes within range. Move nodes closer or increase the radius.
               </div>
            ) : (
               <ItemList items={rangeItems} emptyMessage="No nodes within range." />
            )}
         </ModalSection>
      </DetailModal>
   );
};

export default ResourceNodeDetailModal;
